import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, TrendingUp, Users, Truck, DollarSign, Calendar,
  ShoppingCart, Clock, Activity, CheckCircle, AlertCircle, RefreshCw,
  FileText, Home, ChevronRight
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import apiClient from '../services/api';

// ── Workflow steps config ──────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  { step: 1, label: 'Consignment entry',  short: 'Consignment', route: 'consignments-lt',                       color: '#2563EB', bg: '#EFF6FF', border: '#93C5FD', text: '#1D4ED8' },
  { step: 2, label: 'Status entry',       short: 'Status',      route: 'consignment-status-entry',              color: '#0891B2', bg: '#ECFEFF', border: '#67E8F9', text: '#0E7490' },
  { step: 3, label: 'Payments',           short: 'Payments',    route: 'consignment-payment-without-currency',   color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC', text: '#15803D' },
  { step: 4, label: 'POD',               short: 'POD',         route: 'pod-update',                            color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', text: '#B45309' },
  { step: 5, label: 'Invoice',           short: 'Invoice',     route: 'customer-invoice',                      color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', text: '#B91C1C' },
  { step: 6, label: 'Receipt & payment', short: 'Receipt',     route: 'customer-receipts',                     color: '#2563EB', bg: '#EFF6FF', border: '#93C5FD', text: '#1D4ED8' },
];

// ── WorkflowStrip ─────────────────────────────────────────────────────────
 const WorkflowStrip = ({ onNavigate }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl px-6 py-5 mb-6 shadow-sm">
    {/* Header */}
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-2">
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: '#2563EB' }}
        />
        <span
          className="text-sm font-bold uppercase tracking-widest"
          style={{ color: '#2563EB' }}
        >
          Consignment Workflow
        </span>
      </div>
      <span className="text-xs text-gray-400 font-medium">6 steps</span>
    </div>

    {/* Steps */}
    <div className="flex items-center gap-2">
      {WORKFLOW_STEPS.map((s, i) => (
        <React.Fragment key={s.step}>
          <button
            className="flex-1 border-2 rounded-xl py-4 px-2 text-center cursor-pointer
              transition-all duration-200 hover:-translate-y-1 hover:shadow-lg
              active:translate-y-0 min-w-0 group"
            style={{ background: s.bg, borderColor: s.border }}
            onClick={() => onNavigate(s.route)}
            title={s.label}
          >
            {/* Step number circle */}
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2
                text-sm font-bold shadow-sm group-hover:scale-110 transition-transform duration-200"
              style={{ background: s.color, color: '#fff' }}
            >
              {s.step}
            </div>
            {/* Label */}
            <div
              className="text-[13px] font-semibold leading-tight"
              style={{ color: s.text }}
            >
              {s.short}
            </div>
          </button>

          {/* Arrow */}
          {i < WORKFLOW_STEPS.length - 1 && (
            <ChevronRight size={18} className="text-gray-300 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ── StatCard ──────────────────────────────────────────────────────────────
const ACCENT_CLASSES = {
  blue:   'text-[#2563EB]',
  orange: 'text-[#F59E0B]',
  green:  'text-[#16A34A]',
  teal:   'text-[#0891B2]',
  red:    'text-[#DC2626]',
  slate:  'text-[#1E293B]',
};

const StatCard = ({ color = 'blue', icon: Icon, value, label, note, notePositive }) => {
  const accent = ACCENT_CLASSES[color] || ACCENT_CLASSES.blue;
  return (
    <div className={`bg-white rounded-xl p-6 flex gap-5 items-start relative overflow-hidden shadow-sm border border-[#E2E8F0]
      transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${accent}`}>
      <span className="absolute top-0 left-0 right-0 h-1 bg-current rounded-t-xl" />

      <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'currentColor' }}>
        <Icon size={32} color="white" />
      </div>
      <div className="flex-1">
        <h3 className="text-[2rem] text-[#1E293B] font-bold mb-1">{value}</h3>
        <p className="text-gray-500 text-[0.95rem] mb-2">{label}</p>
        <span
          className={`text-[0.85rem] inline-block px-2 py-1 rounded ${
            notePositive ? 'text-[#16A34A] bg-[#DCFCE7]' : 'text-gray-500 bg-gray-100'
          }`}
        >
          {note}
        </span>
      </div>
    </div>
  );
};

// ── SectionHeader ─────────────────────────────────────────────────────────
const SectionHeader = ({ title, children }) => (
  <div className="flex justify-between items-center mb-5">
    <h3 className="text-[1.25rem] text-[#1E293B] font-semibold">{title}</h3>
    {children}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────
const DashboardHome = ({ sessionData }) => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    customers:    { total: 0, active: 0, inactive: 0, newThisMonth: 0 },
    consignments: { total: 0, pending: 0, inTransit: 0, delivered: 0, thisMonth: 0 },
    financial:    { monthlyRevenue: 0, pendingPayments: 0, totalInvoiced: 0 },
    monthlyTrend: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentConsignments, setRecentConsignments] = useState([]);
  const [loadingConsignments, setLoadingConsignments] = useState(false);

  const session = sessionData?.session || sessionData;

  const isAdmin = () => {
    const userType = session?.usertype?.toLowerCase();
    const isSuperAdmin =
      session?.issuperadmin === true || session?.issuperadmin === 1 || session?.issuperadmin === '1';
    return isSuperAdmin || userType === 'admin' || userType === 'superadmin';
  };

  const hasFullAccess = isAdmin();

  useEffect(() => {
    if (hasFullAccess) loadDashboardData();
    else setLoading(false);
  }, [hasFullAccess]);

  const loadDashboardData = async () => {
    try {
      setLoading(true); setError(null);
      const [statsRes, consRes] = await Promise.all([
        apiClient.get('/dashboard/stats.php'),
        apiClient.get('/dashboard/recent-consignments.php?limit=10'),
      ]);
      if (statsRes.data.success && statsRes.data.data)   setStats(statsRes.data.data.statistics);
      if (consRes.data.success  && consRes.data.data)    setRecentConsignments(consRes.data.data.consignments);
    } catch {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const refreshConsignments = async () => {
    try {
      setLoadingConsignments(true);
      const res = await apiClient.get('/dashboard/recent-consignments.php?limit=10');
      if (res.data.success && res.data.data) setRecentConsignments(res.data.data.consignments);
    } catch { /* silent */ } finally {
      setLoadingConsignments(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => ({
    'Pending': '#F59E0B', 'In Transit': '#2563EB', 'Delivered': '#16A34A',
    'Cancelled': '#DC2626', 'On Hold': '#F59E0B', 'Processing': '#0891B2',
  }[status] || '#9E9E9E');

  const pct = (value, total) => (total === 0 ? 0 : Math.round((value / total) * 100));
  const goTo = (route) => navigate(`/dashboard/${route}`);

  // ── Date strip (shared) ──────────────────────────────────────────────
  const DateStrip = () => (
  <div
    className="flex items-center gap-2 px-4 py-2.5 text-white font-medium rounded-lg mb-6"
    style={{ backgroundColor: '#2563EB' }}
  >
    <Calendar size={20} />
    <span>
      {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
    </span>
  </div>
);
  // ════════════════════════════════════════
  // SIMPLIFIED DASHBOARD — REGULAR USERS
  // ════════════════════════════════════════
  if (!hasFullAccess) {
    return (
      <div className="p-6 lg:p-8 animate-[fadeInUp_0.3s_ease-out_both] min-h-full">
        <DateStrip />
        <WorkflowStrip onNavigate={goTo} />

        {/* Welcome card */}
        <div className="mb-8">
          <div
            className="text-white px-8 py-12 rounded-2xl text-center"
            style={{ background: 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)', boxShadow: '0 10px 30px rgba(37,99,235,0.3)' }}
          >
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-white/20 backdrop-blur-sm">
              <Home size={48} />
            </div>
            <h2 className="text-3xl font-semibold mb-3">Welcome to LTMS</h2>
            <p className="text-lg opacity-95 mb-2">
              Hello, <strong>{session?.fullname || session?.username || 'User'}</strong>
            </p>
            <p className="text-[0.95rem] opacity-85">
              {session?.companyname || 'Company'} • FY {session?.yearname || 'N/A'}
            </p>
          </div>
        </div>

        {/* Quick Access */}
        <h3 className="text-[1.25rem] text-[#1E293B] font-semibold mb-5">Quick Access</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { icon: Package, title: 'My Consignments', desc: 'View your consignments',  route: 'consignment-entry' },
            { icon: FileText, title: 'Reports',         desc: 'Generate reports',         route: 'reports' },
            { icon: Clock,    title: 'Status Updates',  desc: 'Track status updates',     route: 'status-entry' },
            { icon: Users,    title: 'My Profile',      desc: 'View profile details',     route: 'profile' },
          ].map(({ icon: Icon, title, desc, route }) => (
            <div
              key={route}
              onClick={() => goTo(route)}
              className="bg-white p-8 rounded-xl shadow-sm text-center cursor-pointer border-2 border-transparent
                transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-[#2563EB]"
            >
              <Icon size={32} className="text-[#2563EB] mx-auto mb-4" />
              <h4 className="text-[1.2rem] font-semibold text-[#1E293B] mb-2">{title}</h4>
              <p className="text-gray-500 text-sm">{desc}</p>
            </div>
          ))}
        </div>

        {/* Notice */}
        <div className="flex items-start gap-4 p-5 bg-[#F8FAFC] border-l-4 border-[#2563EB] rounded-lg">
          <AlertCircle size={20} className="text-[#2563EB] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#1E293B] text-[0.95rem]">Limited Access</p>
            <p className="text-gray-600 text-[0.95rem] mt-1 leading-relaxed">
              You have standard user access. Contact your administrator for additional permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // FULL DASHBOARD — ADMIN / SUPERADMIN
  // ════════════════════════════════════════
  return (
    <div className="p-6 lg:p-8 animate-[fadeInUp_0.3s_ease-out_both] min-h-full">
      <DateStrip />
      <WorkflowStrip onNavigate={goTo} />

      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-10">
          <Activity size={48} className="text-[#2563EB] animate-spin" />
          <p className="text-[1.1rem] text-gray-500 font-medium">Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <AlertCircle size={48} color="#DC2626" />
          <p className="text-gray-500">{error}</p>
          <button
            onClick={loadDashboardData}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white border-0 rounded-lg px-6 py-3 text-[0.95rem] cursor-pointer transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* ── Customer Stats ── */}
          <SectionHeader title="Customer Statistics" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <StatCard color="blue"   icon={Users}        value={stats.customers.total}
              label="Total Customers"
              note={stats.customers.newThisMonth > 0 ? `+${stats.customers.newThisMonth} new this month` : 'No new customers this month'}
              notePositive={stats.customers.newThisMonth > 0} />
            <StatCard color="green"  icon={CheckCircle}  value={stats.customers.active}
              label="Active Customers"
              note={`${pct(stats.customers.active, stats.customers.total)}% of total`} />
            <StatCard color="orange" icon={AlertCircle}  value={stats.customers.inactive}
              label="Inactive Customers"
              note={stats.customers.inactive > 0 ? 'Requires attention' : 'All active'} />
            <StatCard color="teal"   icon={TrendingUp}   value={stats.customers.newThisMonth}
              label="New This Month"
              note={stats.customers.newThisMonth > 0 ? 'Growing steadily' : 'No growth yet'}
              notePositive={stats.customers.newThisMonth > 0} />
          </div>

          {/* ── Operations Stats ── */}
          <SectionHeader title="Operations Overview" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <StatCard color="blue"   icon={Package}      value={stats.consignments.total}
              label="Total Consignments"   note="All time (this year)" />
            <StatCard color="orange" icon={Clock}        value={stats.consignments.pending}
              label="Pending Consignments"
              note={stats.consignments.pending > 0 ? `${pct(stats.consignments.pending, stats.consignments.total)}% of total` : 'All processed'} />
            <StatCard color="teal"   icon={Truck}        value={stats.consignments.inTransit}
              label="In Transit"
              note={stats.consignments.inTransit > 0 ? `${pct(stats.consignments.inTransit, stats.consignments.total)}% of total` : 'None in transit'} />
            <StatCard color="green"  icon={CheckCircle}  value={stats.consignments.delivered}
              label="Delivered"
              note={`${pct(stats.consignments.delivered, stats.consignments.total)}% success rate`}
              notePositive />
          </div>

          {/* ── Monthly Performance ── */}
          <SectionHeader title="This Month's Performance" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <StatCard color="blue" icon={Package}    value={stats.consignments.thisMonth}
              label="Consignments This Month"
              note={stats.consignments.total > 0 ? `${pct(stats.consignments.thisMonth, stats.consignments.total)}% of yearly total` : 'First of the year'}
              notePositive={stats.consignments.thisMonth > 0} />
            <StatCard color="teal"   icon={DollarSign} value={formatCurrency(stats.financial.monthlyRevenue)}
              label="Monthly Revenue"   note="Total invoiced value" notePositive />
            <StatCard color="orange" icon={Clock}      value={formatCurrency(stats.financial.pendingPayments)}
              label="Pending Payments"
              note={stats.financial.monthlyRevenue > 0
                ? `${pct(stats.financial.pendingPayments, stats.financial.monthlyRevenue)}% of revenue`
                : 'No pending payments'} />
          </div>

          {/* ── Charts ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Status distribution */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#E2E8F0]">
              <h3 className="text-[1.1rem] text-[#1E293B] font-semibold mb-4">Consignment Status Distribution</h3>
              {stats.consignments.total === 0 ? (
                <p className="text-gray-400 text-sm text-center py-16">No data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Pending', value: stats.consignments.pending, color: '#F59E0B' },
                        { name: 'In Transit', value: stats.consignments.inTransit, color: '#2563EB' },
                        { name: 'Delivered', value: stats.consignments.delivered, color: '#16A34A' },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={3}
                    >
                      {[
                        { color: '#F59E0B' },
                        { color: '#2563EB' },
                        { color: '#16A34A' },
                      ].map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Monthly trend */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#E2E8F0]">
              <h3 className="text-[1.1rem] text-[#1E293B] font-semibold mb-4">Consignments & Revenue (6 Months)</h3>
              {(!stats.monthlyTrend || stats.monthlyTrend.length === 0) ? (
                <p className="text-gray-400 text-sm text-center py-16">No data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={formatCurrency} width={80} />
                    <Tooltip formatter={(value, name) => name === 'revenue' ? formatCurrency(value) : value} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Consignments" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="revenue" name="Revenue" fill="#16A34A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>




          {/* ── Recent Consignments ── */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-[#E2E8F0]">
            <SectionHeader title="Recent Consignments">
              <div className="flex items-center gap-3">
                <button
                  onClick={refreshConsignments}
                  disabled={loadingConsignments}
                  className="bg-gray-100 border border-[#E2E8F0] rounded-lg p-2 flex items-center justify-center cursor-pointer
                    transition-all duration-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh consignments"
                >
                  <RefreshCw size={16} className={loadingConsignments ? 'animate-spin' : ''} />
                </button>
                <button
                    className="px-4 py-2 text-white border-0 rounded-lg font-medium cursor-pointer transition-all duration-200
                      hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(37,99,235,0.4)]"
                    style={{ backgroundColor: '#2563EB' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1D4ED8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563EB'; }}
                  >
                    View All
                  </button>
              </div>
            </SectionHeader>

            <div className="overflow-x-auto">
              {loadingConsignments ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <Activity size={24} className="animate-spin text-[#2563EB]" />
                  <p className="text-gray-500 text-sm">Refreshing...</p>
                </div>
              ) : (
                <table className="w-full border-collapse">
                  <thead className="bg-[#F1F5F9] border-b-2 border-[#E2E8F0]">
                    <tr>
                      {['Consignment No', 'Shipper', 'Consignee', 'Packages', 'Status', 'Date'].map(h => (
                        <th
                          key={h}
                          className="text-left px-3 py-3 font-semibold text-gray-700 text-[0.9rem] uppercase tracking-wide"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentConsignments.length > 0 ? (
                      recentConsignments.map(item => (
                        <tr
                          key={item.id}
                          className="border-b border-[#E2E8F0] transition-colors duration-150 hover:bg-[#EFF6FF]"
                        >
                          <td className="px-3 py-4 font-semibold text-[#2563EB]">{item.consignmentNo}</td>
                          <td className="px-3 py-4 text-gray-700">{item.shipper}</td>
                          <td className="px-3 py-4 text-gray-700">{item.consignee}</td>
                          <td className="px-3 py-4 text-gray-700 text-center">{item.packages}</td>
                          <td className="px-3 py-4">
                            <span
                              className="inline-block px-3 py-1.5 rounded-full text-[0.85rem] font-medium text-white text-center whitespace-nowrap"
                              style={{ backgroundColor: item.statusColor || getStatusColor(item.status) }}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-gray-700">{formatDate(item.consignmentDate)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500">
                          No recent consignments found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Quick Actions ── */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#E2E8F0]">
            <h3 className="text-[1.25rem] text-[#1E293B] font-semibold mb-5">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Package,   title: 'New Consignment', desc: 'Create a new consignment entry', route: 'consignment-entry' },
                { icon: Users,     title: 'Add Customer',    desc: 'Register a new customer',        route: 'customers' },
                { icon: Truck,     title: 'Add Vehicle',     desc: 'Add a new vehicle to fleet',     route: 'vehicles' },
                { icon: DollarSign,title: 'View Reports',    desc: 'Generate financial reports',     route: 'reports' },
              ].map(({ icon: Icon, title, desc, route }) => (
                <div
                  key={route}
                  onClick={() => goTo(route)}
                  className="p-5 bg-gradient-to-b from-[#F8FAFC] to-white border-2 border-[#E2E8F0] rounded-xl
                    cursor-pointer text-center transition-all duration-300
                    hover:border-[#2563EB] hover:-translate-y-1 hover:shadow-[0_8px_16px_rgba(37,99,235,0.15)]"
                >
                  <Icon size={24} className="text-[#2563EB] mx-auto mb-3" />
                  <h4 className="text-[1.1rem] text-[#1E293B] font-semibold mb-2">{title}</h4>
                  <p className="text-gray-500 text-[0.9rem]">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardHome;